import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { getCasinoGames, launchCasinoSession, getCasinoProviders } from '../api/api';
import { CasinoGame, CasinoProvider, CasinoProviderListOut } from '../api/types';
import { useTranslation } from '../i18n';

interface CasinoGamesPageProps {
  isLogged: boolean;
  onLoginClick: () => void;
}

const InfiniteScrollTrigger: React.FC<{ onLoadMore: () => void; hasMore: boolean }> = ({ onLoadMore, hasMore }) => {
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    if (!hasMore) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        onLoadMore();
      }
    }, { rootMargin: '800px' });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [hasMore, onLoadMore]);

  return <div ref={ref} className="h-4 w-full" />;
};

const LazyGameCard: React.FC<{ game: CasinoGame; onClick: () => void; className?: string }> = ({ game, onClick, className = '' }) => {
  const [isVisible, setIsVisible] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '400px' }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={`relative rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow cursor-pointer group aspect-[3/4] border border-white/5 bg-[#151a25] ${className}`}
      onClick={onClick}
    >
      {isVisible ? (
        <>
          {game.image_url ? (
            <img
              src={game.image_url}
              alt={game.title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-600 font-black text-xl">
              CASINO
            </div>
          )}

          <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent flex flex-col justify-end p-2 pb-3 text-center pointer-events-none">
            <h3 className="font-bold text-[12px] md:text-[14px] text-white leading-tight mb-[2px] line-clamp-1 drop-shadow-md" title={game.title}>
              {game.title}
            </h3>
          </div>

          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
            <div className="bg-white/20 backdrop-blur-sm rounded-full p-3 shadow-2xl transform scale-75 group-hover:scale-100 transition-transform duration-300">
              <svg className="w-6 h-6 text-white drop-shadow-lg" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};

const ProviderGamesRow: React.FC<{
  provider: CasinoProvider;
  onClickCount: () => void;
  renderGameList: (gameList: CasinoGame[], title: string, countStr?: string, onCountClick?: () => void) => React.ReactNode;
}> = ({ provider, onClickCount, renderGameList }) => {
  const [games, setGames] = React.useState<CasinoGame[]>([]);
  const [hasFetched, setHasFetched] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !hasFetched) {
        setHasFetched(true);
        getCasinoGames({ provider_ref: provider.provider_ref, limit: 20 })
          .then(res => setGames(res.items || []))
          .catch(e => console.error(e));
      }
    }, { rootMargin: '200px' });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [hasFetched, provider.provider_ref]);

  return (
    <div ref={ref} className="min-h-[10px]">
      {hasFetched && games.length > 0 && renderGameList(games, provider.name, `${games.length}+`, onClickCount)}
    </div>
  );
};

export const CasinoGamesPage: React.FC<CasinoGamesPageProps> = ({ isLogged, onLoginClick }) => {
  const { t } = useTranslation();
  const [games, setGames] = useState<CasinoGame[]>([]);
  const [providers, setProviders] = useState<CasinoProvider[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [selectedProvider, setSelectedProvider] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');
  const [isSearchOpen, setIsSearchOpen] = useState<boolean>(false);
  const [activeSessionUrl, setActiveSessionUrl] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'home' | 'grid'>('home');

  const scrollContainer = (e: React.MouseEvent<HTMLButtonElement>, direction: 'left' | 'right') => {
    const container = e.currentTarget.parentElement?.querySelector('.scroll-container');
    if (container) {
      const scrollAmount = 400;
      container.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput);
      if (searchInput) {
        setViewMode('grid');
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    loadProviders();
    loadGames();
  }, []);

  // When filters change, reload games
  useEffect(() => {
    loadGames(true);
  }, [selectedProvider, searchQuery, viewMode]);

  const loadProviders = async () => {
    try {
      let allProviders: CasinoProvider[] = [];
      let cursor: string | null | undefined = undefined;
      let available = false;

      while (true) {
        const res: CasinoProviderListOut = await getCasinoProviders({ limit: 100, after: cursor || undefined }) as any;
        if (res.casino_available) available = true;

        if (res.items && res.items.length > 0) {
          allProviders = [...allProviders, ...res.items];
        }

        if (!res.next_cursor) break;
        cursor = res.next_cursor;
      }

      if (available) {
        const pragmaticProviders = allProviders.filter(p => p.name.toLowerCase().includes('pragmatic'));
        const novomaticProviders = allProviders.filter(p => p.name.toLowerCase().includes('novomatic') || p.name.toLowerCase().includes('novamatic'));
        const boomingProviders = allProviders.filter(p => p.name.toLowerCase().includes('booming'));

        const prioritizedRefs = new Set([
          ...pragmaticProviders.map(p => p.provider_ref),
          ...novomaticProviders.map(p => p.provider_ref),
          ...boomingProviders.map(p => p.provider_ref)
        ]);

        const otherProviders = allProviders.filter(p => !prioritizedRefs.has(p.provider_ref));

        setProviders([...pragmaticProviders, ...novomaticProviders, ...boomingProviders, ...otherProviders]);
      }
    } catch (err: any) {
      console.error('Failed to load casino providers', err);
    }
  };

  const loadGames = async (reset = false) => {
    if (reset) {
      setGames([]);
    }
    setLoading(true);
    setError(null);
    setNextCursor(null);
    try {
      const fetchLimit = (viewMode === 'grid' || searchQuery || selectedProvider) ? 50 : 20;
      const params: any = { limit: fetchLimit };
      if (selectedProvider) params.provider_ref = selectedProvider;
      if (searchQuery) params.search = searchQuery;
      params.device = window.innerWidth < 1024 ? 'mobile' : 'desktop';

      const res = await getCasinoGames(params);
      if (!res.casino_available) {
        setError(t('casino.unavailable') || 'Casino is currently unavailable.');
        setGames([]);
      } else {
        let fetchedGames = res.items || [];
        if (!selectedProvider && !searchQuery) {
          const topGames = [
            {
              game_ref: "cgm_2v-FP_5eh4xsYuudUb_lQ25k",
              title: "Gates of Olympus",
              category: "video slot",
              image_url: "https://cdn.euphoriagames.info/assets/games/Pragmatic/gatesofolympus.jpg",
              provider: { provider_ref: "cpr_D5hbzNBvj-9EWlH2B2hzXW95", name: "Pragmatic" }
            },
            {
              game_ref: "cgm_94bSaz7N1JmcXpk48mVqt2oP",
              title: "Book of Ra Deluxe",
              category: "video slot",
              image_url: "https://cdn.euphoriagames.info/assets/games/Novomatic/bookofradeluxe.jpg",
              provider: { provider_ref: "cpr_pcF790KvX0HVI_EuRcwafwJh", name: "Novomatic" }
            },
            {
              game_ref: "cgm_wjwFLmEiBSI_68bx8UruLC4w",
              title: "Sweet Bonanza",
              category: "video slot",
              image_url: "https://cdn.euphoriagames.info/assets/games/Pragmatic/sweetbonanza.jpg",
              provider: { provider_ref: "cpr_D5hbzNBvj-9EWlH2B2hzXW95", name: "Pragmatic" }
            }
          ];

          const topGameRefs = new Set(topGames.map(g => g.game_ref));
          const excludeTitles = ['gates of olympus', 'book of ra deluxe', 'sweet bonanza'];

          const otherGames = fetchedGames.filter(g =>
            !topGameRefs.has(g.game_ref) &&
            !excludeTitles.includes(g.title.toLowerCase())
          );

          fetchedGames = [...topGames, ...otherGames] as any;
        }

        setGames(fetchedGames);
        setNextCursor(res.next_cursor);
      }
    } catch (err: any) {
      setError(err.message || t('casino.load_failed') || 'Failed to load casino games.');
    } finally {
      setLoading(false);
    }
  };

  const loadMoreGames = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const params: any = { limit: 50, after: nextCursor };
      if (selectedProvider) params.provider_ref = selectedProvider;
      if (searchQuery) params.search = searchQuery;
      params.device = window.innerWidth < 1024 ? 'mobile' : 'desktop';

      const res = await getCasinoGames(params);
      if (res.items) {
        setGames(prev => [...prev, ...res.items]);
        setNextCursor(res.next_cursor);
      }
    } catch (err: any) {
      console.error('Failed to load more games', err);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleLaunchGame = async (gameRef: string) => {
    if (!isLogged) {
      onLoginClick();
      return;
    }

    try {
      const device = window.innerWidth < 1024 ? 'mobile' : 'desktop';
      const res = await launchCasinoSession({ game_ref: gameRef, device });
      if (res.launch_url) {
        setActiveSessionUrl(res.launch_url);
      }
    } catch (err: any) {
      alert((t('casino.launch_error') || 'Could not launch game:') + ' ' + (err.message || t('casino.unknown_error') || 'Unknown error'));
    }
  };

  if (activeSessionUrl) {
    return createPortal(
      <div className="fixed inset-0 z-[99999] bg-black flex flex-col">
        <div className="flex justify-between items-center bg-gray-900 p-3 text-white shadow-md">
          <div className="flex items-center gap-2">
            <svg className="w-6 h-6 text-tipico-primary" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14.5v-9l6 4.5-6 4.5z" />
            </svg>
            <h2 className="font-bold text-lg">{t('casino.title') || 'Casino'}</h2>
          </div>
          <button
            onClick={() => setActiveSessionUrl(null)}
            className="bg-tipico-primary hover:bg-green-600 text-white font-bold py-1 px-4 rounded transition-colors"
          >
            {t('misc.close') || 'Kapat'}
          </button>
        </div>
        <iframe
          src={activeSessionUrl}
          className="flex-1 w-full h-full border-none bg-black"
          title="Casino Game"
          allow="autoplay; fullscreen"
        />
      </div>,
      document.body
    );
  }

  const getProviderLogo = (name: string): string | null => {
    if (!name) return null;
    const normalized = name.toLowerCase().replace(/[^a-z0-9]/g, '');
    
    if (normalized === 'pragmatic' || normalized === 'pragmaticplay') {
      return '/assets/providers/pragmatic-play.svg';
    }
    
    return `/assets/providers/${normalized}.png`;
  };

  const renderGameList = (gameList: CasinoGame[], title: string, countStr?: string, onCountClick?: () => void) => (
    <div className="mb-8" key={title}>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">{title}</h2>
        {countStr && (
          <div
            onClick={onCountClick}
            className="bg-[#242b3d] text-sm px-3 py-1 rounded-full font-medium cursor-pointer hover:bg-[#2d364d] transition-colors"
          >
            {countStr} &gt;
          </div>
        )}
      </div>
      <div className="relative group/slider">
        <button
          onClick={(e) => scrollContainer(e, 'left')}
          className="hidden md:flex absolute left-0 top-[40%] -translate-y-1/2 -ml-3 w-8 h-8 bg-black/60 hover:bg-black/90 text-white rounded-full items-center justify-center z-10 opacity-0 group-hover/slider:opacity-100 transition-opacity border border-white/10 shadow-lg"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
        </button>
        <div className="scroll-container grid grid-rows-2 grid-flow-col gap-3 md:gap-4 overflow-x-auto pb-4 scrollbar-hide">
          {gameList.map((game) => (
            <LazyGameCard
              key={game.game_ref}
              game={game}
              onClick={() => handleLaunchGame(game.game_ref)}
              className="w-[110px] md:w-[160px] flex-shrink-0"
            />
          ))}
        </div>
        <button
          onClick={(e) => scrollContainer(e, 'right')}
          className="hidden md:flex absolute right-0 top-[40%] -translate-y-1/2 -mr-3 w-8 h-8 bg-black/60 hover:bg-black/90 text-white rounded-full items-center justify-center z-10 opacity-0 group-hover/slider:opacity-100 transition-opacity border border-white/10 shadow-lg"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
        </button>
      </div>
    </div>
  );

  return (
    <div className="w-full min-h-screen bg-[#0b0e14] text-white p-4 md:p-6 pb-20">

      {/* 1. Providers Section */}
      <div className="flex justify-between items-center mb-4 mt-2">
        <h2 className="text-xl font-bold">Unsere Anbieter</h2>
        <div className="bg-[#242b3d] text-sm px-3 py-1 rounded-full font-medium cursor-pointer hover:bg-[#2d364d] transition-colors">
          {providers.length} &gt;
        </div>
      </div>

      <div className="relative mb-4 group/slider">
        {/* Sol ok - Sadece desktop */}
        <button
          onClick={(e) => scrollContainer(e, 'left')}
          className="hidden md:flex absolute left-0 top-[40%] -translate-y-1/2 -ml-3 w-8 h-8 bg-black/60 hover:bg-black/90 text-white rounded-full items-center justify-center z-10 opacity-0 group-hover/slider:opacity-100 transition-opacity border border-white/10 shadow-lg"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        <div className="scroll-container flex gap-4 overflow-x-auto pb-6 scrollbar-hide items-center">
          {/* Search Button / Input */}
          <div className={`flex-shrink-0 flex items-center transition-all duration-300 ${isSearchOpen || searchInput ? 'w-[200px] md:w-[250px] bg-white text-black' : 'w-[80px] md:w-[100px] bg-[#1e1e1e] hover:bg-[#2a2a2a]'} h-[60px] md:h-[80px] rounded-xl border ${searchInput ? 'border-tipico-primary' : 'border-transparent'}`}>
          {!isSearchOpen && !searchInput ? (
            <button onClick={() => setIsSearchOpen(true)} className="w-full h-full flex flex-col items-center justify-center gap-1">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-6 h-6 text-gray-400">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <span className="text-[10px] md:text-xs text-gray-400 font-bold uppercase tracking-wider">{t('casino.search') || 'Ara'}</span>
            </button>
          ) : (
            <div className="flex items-center w-full px-3 h-full">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-5 h-5 text-gray-500 shrink-0">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                autoFocus
                type="text"
                placeholder={t('casino.search_placeholder') || 'Oyun adı...'}
                className="bg-transparent border-none outline-none text-black ml-2 w-full text-base h-full placeholder-gray-500"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onBlur={() => {
                  if (!searchInput) setIsSearchOpen(false);
                }}
              />
              {searchInput && (
                <button onClick={() => { setSearchInput(''); setIsSearchOpen(false); setViewMode('home'); }} className="shrink-0 p-1">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-5 h-5 text-gray-500 hover:text-black">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          )}
        </div>

        {providers.map((p) => {
          const logo = getProviderLogo(p.name);
          return (
            <button
              key={p.provider_ref}
              onClick={() => {
                if (selectedProvider === p.provider_ref) {
                  setSelectedProvider('');
                  setViewMode('home');
                } else {
                  setSelectedProvider(p.provider_ref);
                  setViewMode('grid');
                }
              }}
              className={`flex-shrink-0 w-[100px] md:w-[140px] h-[60px] md:h-[80px] rounded-xl flex flex-col items-center justify-center relative border transition-all ${selectedProvider === p.provider_ref ? 'border-tipico-primary bg-[#1e1e1e]' : 'border-transparent bg-[#1e1e1e] hover:bg-[#2a2a2a]'
                }`}
            >
              {logo ? (
                <img src={logo} alt={p.name} className="h-[3.5rem] md:h-[5rem] w-auto object-contain p-1" />
              ) : (
                <span className="font-bold text-gray-200 text-[11px] md:text-[14px] text-center px-2 line-clamp-2 tracking-wide">{p.name.toUpperCase()}</span>
              )}
            </button>
          )
        })}
        </div>

        {/* Sağ ok - Sadece desktop */}
        <button
          onClick={(e) => scrollContainer(e, 'right')}
          className="hidden md:flex absolute right-0 top-[40%] -translate-y-1/2 -mr-3 w-8 h-8 bg-black/60 hover:bg-black/90 text-white rounded-full items-center justify-center z-10 opacity-0 group-hover/slider:opacity-100 transition-opacity border border-white/10 shadow-lg"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* 2. Top 10 Slots Section */}
      {!loading && !error && games.length > 0 && viewMode === 'home' && !searchQuery && (
        <div className="mb-8 mt-2 overflow-visible">
          <div className="relative group/slider">
            <button
              onClick={(e) => scrollContainer(e, 'left')}
              className="hidden md:flex absolute left-0 top-[45%] -translate-y-1/2 ml-1 w-8 h-8 bg-black/60 hover:bg-black/90 text-white rounded-full items-center justify-center z-20 opacity-0 group-hover/slider:opacity-100 transition-opacity border border-white/10 shadow-lg"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
            </button>
            <div className="scroll-container flex gap-[36px] md:gap-[46px] overflow-x-auto pb-6 pt-4 scrollbar-hide pl-8 md:pl-12 pr-4 md:pr-6 -mx-4 md:-mx-6">
              {games.slice(0, 10).map((game, index) => (
              <div
                key={`top-${game.game_ref}`}
                className="relative flex-shrink-0 w-[115px] md:w-[160px] aspect-[3/4] rounded-xl shadow-sm hover:shadow-lg transition-shadow cursor-pointer group"
                onClick={() => handleLaunchGame(game.game_ref)}
              >
                <div
                  className="absolute -left-[28px] md:-left-[36px] bottom-0 text-[70px] md:text-[130px] font-black italic leading-none z-10 pointer-events-none drop-shadow-2xl"
                  style={{
                    WebkitTextStroke: '2px rgba(255,255,255,0.7)',
                    color: 'rgba(255,255,255,0.1)',
                  }}
                >
                  {index + 1}
                </div>

                {/* Game Card */}
                <div className="w-full h-full rounded-xl overflow-hidden relative border border-white/5 bg-[#151a25]">
                  {game.image_url ? (
                    <img
                      src={game.image_url}
                      alt={game.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-600 font-black text-xl">
                      CASINO
                    </div>
                  )}

                  {/* Gradient Overlay for Text */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent flex flex-col justify-end p-2 pb-3 text-center pointer-events-none">
                    <h3 className="font-bold text-[13px] md:text-[14px] text-white leading-tight mb-[2px] line-clamp-1 drop-shadow-md">
                      {game.title}
                    </h3>
                  </div>
                </div>
              </div>
            ))}
            </div>
            <button
              onClick={(e) => scrollContainer(e, 'right')}
              className="hidden md:flex absolute right-0 top-[45%] -translate-y-1/2 mr-1 w-8 h-8 bg-black/60 hover:bg-black/90 text-white rounded-full items-center justify-center z-20 opacity-0 group-hover/slider:opacity-100 transition-opacity border border-white/10 shadow-lg"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
            </button>
          </div>
        </div>
      )}

      {/* 3. Slots Sections */}
      {error && (
        <div className="bg-red-900/40 text-red-200 p-4 rounded-lg mb-6 border border-red-800/50">
          {error}
        </div>
      )}

      {loading && games.length === 0 ? (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-tipico-primary"></div>
        </div>
      ) : games.length === 0 && !error ? (
        <div className="text-center py-20 text-gray-500">
          {t('casino.not_found') || 'Oyun bulunamadı.'}
        </div>
      ) : (
        <div className="mt-4">
          {viewMode === 'grid' || searchQuery ? (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">
                  {searchQuery ? 'Arama Sonuçları' : selectedProvider ? providers.find(p => p.provider_ref === selectedProvider)?.name : 'Alle Slots'}
                </h2>
                <div
                  className="bg-[#242b3d] text-sm px-4 py-1.5 rounded-full font-bold cursor-pointer hover:bg-[#2d364d] transition-colors flex items-center gap-2"
                  onClick={() => { setViewMode('home'); setSelectedProvider(''); }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path></svg>
                  {t('misc.back') || 'Geri'}
                </div>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-7 gap-3 md:gap-4">
                {games.map(game => (
                  <LazyGameCard
                    key={`grid-${game.game_ref}`}
                    game={game}
                    onClick={() => handleLaunchGame(game.game_ref)}
                  />
                ))}
              </div>
            </div>
          ) : (
            <>
              {/* Main All Slots list */}
              {renderGameList(games, "Alle Slots", `${games.length}+`, () => {
                setSelectedProvider('');
                setViewMode('grid');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              })}

              {/* Provider Specific Lists */}
              {!searchQuery && !selectedProvider && providers.map(p => (
                <ProviderGamesRow
                  key={p.provider_ref}
                  provider={p}
                  renderGameList={renderGameList}
                  onClickCount={() => { setSelectedProvider(p.provider_ref); setViewMode('grid'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                />
              ))}
            </>
          )}
        </div>
      )}

      {nextCursor && games.length > 0 && !loading && !error && (
        <div className="flex justify-center mt-10 mb-4">
          <button
            onClick={loadMoreGames}
            disabled={loadingMore}
            className="px-6 py-2.5 bg-[#242b3d] hover:bg-[#2d364d] text-white text-sm font-bold rounded-full shadow transition-colors disabled:opacity-50 flex items-center justify-center gap-2 min-w-[150px]"
          >
            {loadingMore && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white/50"></div>}
            {t('misc.load_more') || 'Daha Fazla Yükle'}
          </button>
        </div>
      )}
    </div>
  );
};
